import { create } from 'zustand';

const LS_KEY = 'rt.currentProject';

export type ProjectRole = 'owner' | 'moderator' | 'editor' | 'viewer';

interface PersistedShape {
  id: string | null;
  role: ProjectRole | null;
  appKey: string | null;
}

interface ProjectState {
  projectId: string | null;
  projectRole: ProjectRole | null;
  appKey: string | null;
  setProject: (id: string, role: ProjectRole | null) => void;
  /** Sync just the role when the projects list comes back; keeps id untouched. */
  syncRole: (id: string, role: ProjectRole | null) => void;
  /** Sets the active app. A project belongs to one app, so switching to a
   *  different app clears the currently selected project. */
  setApp: (appKey: string | null) => void;
  /** Clears the selected project (e.g. a stale cross-app selection) without
   *  touching the active app. */
  clearProject: () => void;
  clear: () => void;
}

function readInitial(): PersistedShape | null {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (
      parsed &&
      typeof parsed === 'object' &&
      'id' in parsed &&
      ((parsed as PersistedShape).id === null || typeof (parsed as PersistedShape).id === 'string')
    ) {
      const shape = parsed as PersistedShape;
      return { id: shape.id, role: shape.role ?? null, appKey: shape.appKey ?? null };
    }
    return null;
  } catch {
    return null;
  }
}

function persist(next: PersistedShape | null): void {
  try {
    if (next) localStorage.setItem(LS_KEY, JSON.stringify(next));
    else localStorage.removeItem(LS_KEY);
  } catch {
    /* ignore */
  }
}

const initial = readInitial();

export const useProjectStore = create<ProjectState>((set, get) => ({
  projectId: initial?.id ?? null,
  projectRole: initial?.role ?? null,
  appKey: initial?.appKey ?? null,
  setProject: (id, role) => {
    persist({ id, role, appKey: get().appKey });
    set({ projectId: id, projectRole: role });
  },
  syncRole: (id, role) => {
    const current = get();
    if (current.projectId !== id) return;
    if (current.projectRole === role) return;
    persist({ id, role, appKey: current.appKey });
    set({ projectRole: role });
  },
  setApp: (appKey) => {
    const current = get();
    if (current.appKey === appKey) return; // no change

    // A project belongs to one app, so switching to a different app can't
    // carry the old selection over.
    set({ appKey, projectId: null, projectRole: null });

    if (appKey == null) {
      persist(null);
    } else {
      persist({ id: null, role: null, appKey });
    }
  },
  clearProject: () => {
    const current = get();
    persist({ id: null, role: null, appKey: current.appKey });
    set({ projectId: null, projectRole: null });
  },
  clear: () => {
    persist(null);
    set({ projectId: null, projectRole: null, appKey: null });
  },
}));
